<template>
  <div id="app">
    <!-- Navigation Bar -->
    <nav class="navbar navbar-expand-lg navbar-light fixed-top" :class="{
      'bg-transparent': isHome && !scrolled, 
      'bg-glass': !isHome || scrolled
    }">
      <div class="container">
        <router-link to="/" class="navbar-brand d-flex align-items-center">
          <div class="brand-logo">
            <div class="logo-helix">
              <div class="helix-strand"></div>
              <div class="helix-strand"></div>
              <div class="helix-strand"></div>
            </div>
          </div>
          <div class="brand-text ms-2">
            <span class="fw-bold" :class="{'text-gradient': !scrolled, 'text-white': scrolled}">PEIST</span>
            <span class="ms-1 biotech-text" :class="{'text-white': scrolled}">Biotech IP</span>
          </div>
        </router-link>

        <button 
          class="navbar-toggler custom-toggler" 
          type="button" 
          data-bs-toggle="collapse" 
          data-bs-target="#navbarNav" 
          aria-controls="navbarNav" 
          aria-expanded="false" 
          aria-label="Toggle navigation"
        >
          <div class="hamburger-icon">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </button>

        <div class="collapse navbar-collapse" id="navbarNav">
          <ul class="navbar-nav ms-auto">
            <li class="nav-item">
              <router-link to="/" class="nav-link px-3" active-class="active">
                <span class="nav-text">Home</span>
                <span class="nav-indicator"></span>
              </router-link>
            </li>
            <li class="nav-item">
              <router-link to="/about" class="nav-link px-3" active-class="active">
                <span class="nav-text">About</span>
                <span class="nav-indicator"></span>
              </router-link>
            </li>
            <li class="nav-item">
              <router-link to="/services" class="nav-link px-3" active-class="active">
                <span class="nav-text">Services</span>
                <span class="nav-indicator"></span>
              </router-link>
            </li>
            <li class="nav-item">
              <router-link to="/contact" class="nav-link px-3 me-3" active-class="active">
                <span class="nav-text">Contact</span>
                <span class="nav-indicator"></span>
              </router-link>
            </li>
            <li class="nav-item">
              <router-link to="/contact" class="btn btn-gradient btn-sm nav-btn">
                <span>Get Started</span>
                <div class="btn-glow"></div>
              </router-link>
            </li>

          </ul>
        </div>
      </div>
    </nav>

    <!-- Main Content -->
    <main :class="{'pt-5': !isHome}">
      <router-view />
    </main>

    <!-- Footer -->
    <footer class="footer-light">
      <div class="footer-content py-4">
        <div class="container">
          <div class="row align-items-center">
            <div class="col-lg-4 mb-3 mb-lg-0">
              <div class="d-flex align-items-center justify-content-center justify-content-lg-start">
                <div class="brand-logo-footer me-2">
                  <div class="logo-helix-footer">
                    <div class="helix-strand-footer"></div>
                    <div class="helix-strand-footer"></div>
                  </div>
                </div>
                <div class="brand-text-footer">
                  <span class="fw-bold text-gradient">PEIST</span>
                  <span class="ms-1 biotech-text-footer">Biotech IP</span>
                </div>
              </div>
            </div>
            <div class="col-lg-7 offset-lg-1">
              <div class="d-flex flex-wrap justify-content-center justify-content-lg-end gap-4">
                <router-link to="/" class="footer-nav-link">Home</router-link>
                <router-link to="/about" class="footer-nav-link">About</router-link>
                <router-link to="/services" class="footer-nav-link">Services</router-link>
                <router-link to="/contact" class="footer-nav-link">Contact</router-link>
                <a href="https://www.linkedin.com/in/ken-peist-3799789/" class="footer-nav-link" target="_blank" rel="noopener">
                  <i class="bi bi-linkedin me-1"></i>LinkedIn
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="footer-bottom py-3">
        <div class="container">
          <div class="row">
            <div class="col-12 text-center">
              <p class="mb-0 text-muted small">&copy; 2025 PEIST Biotech IP Consulting. All rights reserved.</p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
export default {
  name: 'App',
  data() {
    return {
      scrolled: false,
      isHome: false
    }
  },
  created() {
    this.isHome = this.$route.path === '/';
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll);
    this.$watch(
      () => this.$route.path,
      (path) => {
        this.isHome = path === '/';
      }
    );
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
  },
  methods: {
    handleScroll() {
      this.scrolled = window.scrollY > 50;
    }
  }
}
</script>

<style>
@import url('https://cdn.jsdelivr.net/npm/bootstrap-icons@1.10.0/font/bootstrap-icons.css');
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800&family=Roboto:wght@300;400;500;700&display=swap');

:root {
  /* Common colors */
  --primary: #0A84FF;
  --primary-dark: #0064D0;
  --secondary: #5E5CE6;
  --gradient-start: #0A84FF;
  --gradient-end: #5E5CE6;
  --transition: all 0.3s ease;
  
  /* Light theme colors (default) */
  --bg-color: #FFFFFF;
  --bg-secondary: #F5F7FA;
  --bg-tertiary: #E9ECEF;
  --text-color: #212529;
  --text-muted: #6C757D;
  --border-color: #DEE2E6;
  --card-bg: #FFFFFF;
  --card-border: rgba(0, 0, 0, 0.05);
  --navbar-bg: rgba(255, 255, 255, 0.9);
  --footer-bg: #F8F9FA;
  --footer-text: #495057;
}

/* Footer light theme */
.footer-light {
  background-color: #f8f9fa;
  color: #333;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
}

body {
  background-color: var(--bg-color);
  color: var(--text-color);
  margin: 0;
  padding: 0;
  overflow-x: hidden;
  transition: background-color 0.3s ease, color 0.3s ease;
}

#app {
  font-family: 'Montserrat', 'Roboto', sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

main {
  flex: 1;
  padding-top: 76px; /* Height of navbar */
}

/* Navbar Styles */
.navbar-nav {
  gap: 0.5rem;
  display: flex;
  align-items: center;
}

/* Mobile Menu Styles */
@media (max-width: 991.98px) {
  /* Basic navbar styling */
  .navbar {
    background-color: white !important;
    padding: 0.75rem 0;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  }
  
  /* Container padding */
  .navbar > .container {
    padding-left: 1rem;
    padding-right: 1rem;
  }
  
  /* Custom hamburger button styling */
  .navbar-toggler {
    border: 2px solid #0078FF;
    padding: 0.5rem;
    background-color: white;
    border-radius: 8px;
    min-width: 48px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  /* Hide default toggler icon */
  .navbar-toggler-icon {
    display: none;
  }
  
  /* Custom hamburger icon */
  .hamburger-icon {
    width: 24px;
    height: 20px;
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }
  
  /* Hamburger bars */
  .hamburger-icon span {
    display: block;
    height: 3px;
    width: 100%;
    background-color: #0078FF;
    border-radius: 3px;
  }
  
  /* Dropdown menu container */
  .navbar-collapse {
    background-color: white;
    margin-top: 0.75rem;
    border-radius: 8px;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
    overflow: hidden;
  }
  
  /* Navigation items list */
  .navbar-nav {
    padding: 0.5rem;
    margin: 0;
  }
  
  /* Individual nav items */
  .nav-item {
    margin: 0.25rem 0;
    width: 100%;
  }
  
  /* Last nav item (Get Started button) */
  .nav-item:last-child {
    margin-top: 0.75rem;
    padding: 0.5rem;
  }
  
  /* Navigation links */
  .nav-link {
    padding: 0.75rem 1rem;
    border-radius: 8px;
    color: var(--text-color) !important;
    transition: background-color 0.2s ease;
  }
  
  /* Active and hover states for nav links */
  .nav-link:hover,
  .nav-link.active {
    background-color: rgba(0, 120, 255, 0.1);
    color: var(--primary) !important;
  }
  
  /* Get Started button in mobile menu */
  .nav-btn {
    width: 100%;
    justify-content: center;
    padding: 0.75rem;
    font-size: 1rem;
  }
}

.nav-item {
  display: flex;
  align-items: center;
}

/* This section is now handled in the main mobile menu styles above */

.navbar {
  padding: 1rem 0;
  transition: var(--transition);
  z-index: 1000;
}

.bg-glass {
  background-color: rgba(255, 255, 255, 0.9) !important;
  backdrop-filter: blur(15px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

/* Brand Logo and Text */
.navbar-brand {
  font-weight: 700;
  font-size: 1.5rem;
  display: flex;
  align-items: center;
}

.brand-logo {
  width: 40px;
  height: 40px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.logo-helix {
  width: 100%;
  height: 100%;
  position: relative;
  transform-style: preserve-3d;
  animation: rotateLogo 8s linear infinite;
}

@keyframes rotateLogo {
  0% { transform: rotateY(0deg); }
  100% { transform: rotateY(360deg); }
}

.helix-strand {
  position: absolute;
  width: 100%;
  height: 4px;
  background: linear-gradient(90deg, var(--gradient-start), var(--gradient-end));
  border-radius: 2px;
  transform-style: preserve-3d;
}

.helix-strand:nth-child(1) {
  top: 10px;
  transform: rotateY(0deg) translateZ(8px);
}

.helix-strand:nth-child(2) {
  top: 20px;
  transform: rotateY(60deg) translateZ(8px);
}

.helix-strand:nth-child(3) {
  top: 30px;
  transform: rotateY(120deg) translateZ(8px);
}

.brand-text {
  display: flex;
  flex-direction: column;
  line-height: 1;
}

.biotech-text {
  font-size: 0.9rem;
  color: var(--text-muted);
  letter-spacing: 1px;
}

/* Navigation Links */
.nav-link {
  font-weight: 500;
  position: relative;
  transition: var(--transition);
  padding: 0.5rem 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* This section is now handled in the main mobile menu styles above */

.nav-text {
  position: relative;
  z-index: 2;
}

.nav-indicator {
  position: absolute;
  bottom: 0;
  left: 50%;
  width: 0;
  height: 2px;
  background: linear-gradient(to right, var(--gradient-start), var(--gradient-end));
  transition: width 0.3s ease;
  transform: translateX(-50%);
  opacity: 0;
  border-radius: 1px;
}

/* This section is now handled in the main mobile menu styles above */

.nav-link:hover .nav-indicator,
.nav-link.active .nav-indicator {
  width: 30px;
  opacity: 1;
}

.nav-link:hover {
  color: var(--primary) !important;
}

.nav-link.active {
  color: var(--gradient-end) !important;
}

/* Get Started Button */
.nav-btn {
  position: relative;
  padding: 0.6rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  letter-spacing: 0.5px;
  overflow: hidden;
  z-index: 1;
  border: none;
  background: linear-gradient(to right, var(--gradient-start), var(--gradient-end));
  color: white;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
}

/* This section is now handled in the main mobile menu styles above */

.nav-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 20px rgba(10, 132, 255, 0.3);
}

.btn-glow {
  position: absolute;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(10, 132, 255, 0.5);
  filter: blur(15px);
  animation: btnGlow 2s infinite;
  z-index: -1;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

@keyframes btnGlow {
  0% {
    transform: translate(-50%, -50%) scale(0.8);
    opacity: 0.5;
  }
  50% {
    transform: translate(-50%, -50%) scale(1.2);
    opacity: 0.2;
  }
  100% {
    transform: translate(-50%, -50%) scale(0.8);
    opacity: 0.5;
  }
}

/* Theme Toggle Button */
.theme-toggle-btn {
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.toggle-track {
  width: 50px;
  height: 24px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 2px;
  position: relative;
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.toggle-indicator {
  width: 20px;
  height: 20px;
  background: linear-gradient(135deg, var(--gradient-start), var(--gradient-end));
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 12px;
  transition: all 0.3s ease;
  transform: translateX(0);
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
}

.toggle-indicator.active {
  transform: translateX(26px);
}

.toggle-indicator i {
  line-height: 1;
}

/* Mobile Navigation */
.navbar-toggler {
  border: none;
  padding: 0;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
}

.navbar-toggler:focus {
  box-shadow: none;
}

.navbar-toggler-icon {
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 30 30'%3e%3cpath stroke='rgba(255, 255, 255, 1)' stroke-linecap='round' stroke-miterlimit='10' stroke-width='2' d='M4 7h22M4 15h22M4 23h22'/%3e%3c/svg%3e") !important;
}

/* Footer Styles */
.footer-dark {
  background-color: var(--footer-bg);
  padding: 3rem 0 1.5rem;
  color: var(--text-color);
  margin-top: 5rem;
  transition: background-color 0.3s ease, color 0.3s ease;
}

.footer-top {
  position: relative;
}

.footer-top::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
}

.footer-circuit {
  position: absolute;
  top: 0;
  right: 0;
  width: 100%;
  height: 100%;
  background-image: url('data:image/svg+xml;base64,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');
  background-size: 200px 200px;
  opacity: 0.1;
  z-index: 0;
}

.footer-nav-link {
  color: var(--dark);
  text-decoration: none;
  transition: all 0.3s ease;
  position: relative;
  font-size: 0.9rem;
  padding: 0.25rem 0;
}

.footer-nav-link::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 0;
  width: 0;
  height: 1px;
  background: var(--primary);
  transition: width 0.3s ease;
}

.footer-nav-link:hover {
  color: var(--primary);
}

.footer-nav-link:hover::after {
  width: 100%;
}

.footer-divider-top {
  width: 100%;
  height: 48px;
  overflow: hidden;
  position: relative;
  transform: rotate(180deg);
  margin-bottom: -1px;
}

.footer-content {
  background-color: rgba(255, 255, 255, 1);
  position: relative;
  z-index: 2;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
}

.footer-light {
  background-color: #ffffff;
  color: var(--dark);
  position: relative;
  overflow: hidden;
  margin-top: 3rem;
  box-shadow: 0 -5px 15px rgba(0, 0, 0, 0.05);
}

.contact-icon i {
  color: var(--primary);
  font-size: 1rem;
  z-index: 1;
}

.contact-icon::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 50%;
  border: 1px solid rgba(var(--primary-rgb), 0.3);
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  100% {
    transform: scale(1.5);
    opacity: 0;
  }
}

.contact-text p {
  color: var(--light-muted);
  font-size: 0.9rem;
}

.brand-logo-footer {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
}

.logo-helix-footer {
  position: relative;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  transform: rotate(30deg);
}

.helix-strand-footer {
  position: absolute;
  width: 100%;
  height: 5px;
  background: linear-gradient(90deg, var(--primary), var(--secondary));
  border-radius: 10px;
  transform-origin: center;
}

.helix-strand-footer:first-child {
  transform: rotate(45deg);
}

.helix-strand-footer:last-child {
  transform: rotate(-45deg);
}

.brand-text-footer {
  display: inline-flex;
  align-items: center;
}

.biotech-text-footer {
  color: var(--dark);
  position: relative;
  overflow: hidden;
}

.social-links {
  display: flex;
  gap: 15px;
}

.social-link {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.05);
  color: var(--light);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.social-glow {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: radial-gradient(circle at center, rgba(var(--primary-rgb), 0.5) 0%, rgba(var(--primary-rgb), 0) 70%);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.social-link:hover {
  background: rgba(var(--primary-rgb), 0.1);
  color: var(--primary);
  transform: translateY(-3px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
}

.social-link:hover .social-glow {
  opacity: 0.5;
}

.footer-divider {
  width: 100%;
  height: 48px;
  overflow: hidden;
  position: relative;
}

.footer-bottom {
  background-color: rgba(0, 0, 0, 0.2);
  position: relative;
  z-index: 2;
}

.hover-effect {
  position: relative;
  transition: all 0.3s ease;
}

.hover-effect::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 0;
  width: 0;
  height: 1px;
  background: var(--primary);
  transition: width 0.3s ease;
}

.hover-effect:hover {
  color: var(--primary) !important;
}

.hover-effect:hover::after {
  width: 100%;
}

/* Theme toggle button */
.theme-toggle {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: var(--transition);
  background-color: transparent;
  border: 1px solid var(--primary);
  color: var(--primary);
}

.theme-toggle:hover {
  background-color: var(--primary);
  color: white;
  transform: translateY(-3px);
}

.social-link:hover {
  background: linear-gradient(to right, var(--gradient-start), var(--gradient-end));
  transform: translateY(-3px);
  color: var(--light);
}

.footer-bottom {
  background-color: rgba(0, 0, 0, 0.2);
  font-size: 0.9rem;
}

.text-secondary {
  color: var(--light-muted);
}

/* Override Bootstrap */
.bg-primary {
  background-color: var(--primary) !important;
}

.btn-primary {
  background-color: var(--primary);
  border-color: var(--primary);
  color: #fff;
  font-weight: 600;
  border-radius: 8px;
}

.btn-primary:hover, .btn-primary:focus {
  background-color: var(--primary-dark);
  border-color: var(--primary-dark);
}

.btn-gradient {
  background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
  border: none;
  color: #fff;
  font-weight: 600;
  border-radius: 8px;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 120, 255, 0.2);
}

.btn-gradient:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 120, 255, 0.3);
  color: white;
}

/* Animation Classes */
.fade-in {
  animation: fadeIn 1s ease-in-out;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Responsive adjustments */
@media (max-width: 992px) {
  .navbar {
    padding: 0.75rem 0;
    background-color: rgba(10, 10, 10, 0.95) !important;
  }
}
</style>
